import { cn } from '@/lib/utils';

/**
 * Courbe miniature — douze mois d'histoire dans la hauteur d'une ligne.
 *
 * Dessinée à la main plutôt qu'avec Recharts : une tuile de statistique n'a
 * pas d'axes, pas d'infobulle et pas de survol, et un conteneur réactif par
 * tuile coûterait quatre observateurs de redimensionnement pour quatre
 * courbes de trente pixels.
 *
 * Le tracé est **aplati horizontalement** (`preserveAspectRatio="none"`) pour
 * occuper toute la largeur quelle qu'elle soit ; `vectorEffect` garde le trait
 * à son épaisseur réelle, sans quoi l'étirement l'épaissirait.
 *
 * La base part de zéro et non du minimum : sur des recettes, une échelle qui
 * commence au plus bas mois transforme une variation de 2 % en falaise.
 */
export function Sparkline({
  valeurs,
  className,
  titre,
}: {
  valeurs: number[];
  className?: string;
  /** Description pour les lecteurs d'écran — la courbe est muette sans elle. */
  titre?: string;
}) {
  // Deux points font une ligne ; un seul ne fait rien.
  if (valeurs.length < 2 || valeurs.every((v) => v === 0)) return null;

  const haut = Math.max(...valeurs) || 1;
  const pas = 100 / (valeurs.length - 1);
  // 1 et 29 : une marge d'un pixel en haut et en bas pour que le trait ne soit
  // pas coupé par le bord de la zone de dessin.
  const y = (v: number) => 29 - (Math.max(0, v) / haut) * 28;

  const points = valeurs.map((v, i) => `${(i * pas).toFixed(2)},${y(v).toFixed(2)}`).join(' ');
  const dernier = valeurs[valeurs.length - 1] ?? 0;

  return (
    <svg
      viewBox="0 0 100 30"
      preserveAspectRatio="none"
      role={titre ? 'img' : 'presentation'}
      aria-label={titre}
      className={cn('h-8 w-full', className)}
    >
      <polygon points={`${points} 100,30 0,30`} className="fill-mustard-200/60" />
      <polyline
        points={points}
        fill="none"
        vectorEffect="non-scaling-stroke"
        strokeWidth={1.5}
        strokeLinejoin="round"
        className="stroke-mustard-500"
      />
      {/* Le dernier point : c'est le mois en cours, celui qu'on cherche. */}
      <circle cx={100} cy={y(dernier)} r={2} vectorEffect="non-scaling-stroke" className="fill-mustard-600" />
    </svg>
  );
}
